"use client";
import React, { useState } from "react";
import Image from "next/image";
import Icon1 from "@theme3/assets/img/newsletter/newsletter-icon-1.png";
import Icon2 from "@theme3/assets/img/newsletter/newsletter-icon-2.png";
import Icon3 from "@theme3/assets/img/newsletter/newsletter-icon-3.png";
import Icon4 from "@theme3/assets/img/newsletter/newsletter-icon-4.png";
import Icon5 from "@theme3/assets/img/newsletter/newsletter-icon-5.png";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { apiVerifyCaptcha } from "@/apis/captcha";
import { apiSubscribeNewsletter } from "@/apis/page_optimization";

interface Props {
    companyId: string;
    domain: string;
}

const Newsletter = ({ companyId, domain }: Props) => {
    const [email, setEmail] = useState<string>("");
    const [status, setStatus] = useState<"idle" | "verifying" | "sending" | "failed">("idle");

    const { executeRecaptcha } = useGoogleReCaptcha();

    const buttonLabel = {
        idle:      "Subscribe Now",
        verifying: "Verifying...",
        sending:   "Subscribing...",
        failed:    "Failed. Try Again",
    }[status];

    const validateEmail = (val: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error("Email field is required.", { autoClose: 2000 });
            return;
        }
        if (!validateEmail(email)) {
            toast.error("Please enter a valid email address.", { autoClose: 2000 });
            return;
        }
        if (!executeRecaptcha) {
            toast.error("reCAPTCHA not ready. Please try again.", { autoClose: 2000 });
            return;
        }

        try {
            // Step 1 — verify reCAPTCHA
            setStatus("verifying");
            const token = await executeRecaptcha("newsletter_form");
            const captchaRes = await apiVerifyCaptcha(domain, token);

            if (!captchaRes?.verified) {
                setStatus("failed");
                toast.error("Captcha verification failed. Please try again.", { autoClose: 2000 });
                return;
            }

            // Step 2 — subscribe
            setStatus("sending");
            const response = await apiSubscribeNewsletter(companyId, email);

            if (response?.status === 200 || response?.message === "Subscribed successfully") {
                toast.success("Thank you for subscribing!", { autoClose: 2000 });
                setEmail("");
                setStatus("idle");
            } else {
                toast.error("You have already subscribed to our newsletter.");
                setStatus("idle");
            }
        } catch (error) {
            console.error("Error subscribing:", error);
            toast.error("Failed to subscribe. Please try again later.", { autoClose: 2000 });
            setStatus("failed");
        }
    };

    const isLoading = status === "verifying" || status === "sending";

    return (
        <section className="relative py-12 px-4 md:py-16 bg-white overflow-hidden">
            {/* Subtle brand skew background */}
            <div className="absolute top-0 right-0 w-1/3 h-full bg-[#D61C4E]/[0.02] -skew-x-12 transform origin-top pointer-events-none" />

            <div className="container mx-auto max-w-6xl relative">

                {/* ── Animated floating icons ── */}
                <div className="hidden xl:block pointer-events-none">
                    <div className="absolute -top-10 -left-10 opacity-20 hover:opacity-100 transition-all duration-700 animate-[bounce_4s_infinite]">
                        <Image src={Icon1} height={110} width={110} alt="icon" />
                    </div>
                    <div className="absolute -bottom-6 right-10 opacity-20 hover:opacity-100 transition-all duration-700 animate-[pulse_3s_infinite]">
                        <Image src={Icon5} height={130} width={130} alt="icon" />
                    </div>
                    <div className="absolute top-1/2 -right-20 -translate-y-1/2 opacity-10 blur-sm">
                        <Image src={Icon2} height={150} width={150} alt="icon" />
                    </div>
                    <div className="absolute top-4 left-1/3 opacity-10 animate-[bounce_6s_infinite_1s]">
                        <Image src={Icon3} height={70} width={70} alt="icon" />
                    </div>
                    <div className="absolute bottom-4 left-1/4 opacity-10 animate-[pulse_5s_infinite_0.5s]">
                        <Image src={Icon4} height={80} width={80} alt="icon" />
                    </div>
                </div>

                {/* ── Main card ── */}
                <div className="relative group">
                    {/* Glow shadow behind card */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#D61C4E] to-[#000000] rounded-[3rem] blur-2xl opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-500" />

                    <div className="relative bg-white border border-gray-100 rounded-[2rem] px-8 py-10 md:px-14 md:py-12 shadow-sm overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">

                        {/* LEFT — copy */}
                        <div className="relative z-10 lg:w-1/2 text-center lg:text-left">
                            <div className="inline-block px-3 py-1 mb-3 rounded-full bg-[#D61C4E]/5 border border-[#D61C4E]/10">
                                <span className="text-[#D61C4E] text-[9px] font-black uppercase tracking-[3px]">
                                    Weekly Newsletter
                                </span>
                            </div>

                            <h2 className="text-2xl md:text-3xl font-black text-gray-900 leading-[1.2] mb-3">
                                Catch the{" "}
                                <span className="text-[#D61C4E] relative">
                                    Best Deals
                                    <svg
                                        className="absolute -bottom-1.5 left-0 w-full"
                                        viewBox="0 0 200 8"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1 6C50 1.5 150 1.5 199 6"
                                            stroke="#D61C4E"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </span>
                            </h2>

                            <p className="text-gray-500 text-sm font-medium max-w-sm mx-auto lg:mx-0 leading-relaxed">
                                Join 50k+ savvy shoppers and get premium offers delivered
                                straight to your inbox.
                            </p>
                        </div>

                        {/* RIGHT — form */}
                        <div className="lg:w-[45%] w-full relative z-10">
                            <form
                                onSubmit={handleSubmit}
                                className="group/form relative flex flex-col gap-3"
                            >
                                {/* Email input */}
                                <div className="relative flex items-center">
                                    <div className="absolute left-4 text-gray-400 group-focus-within/form:text-[#D61C4E] transition-colors pointer-events-none">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                    <input
                                        type="email"
                                        placeholder="Email Address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading}
                                        className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-transparent focus:bg-white focus:border-[#D61C4E]/20 focus:ring-4 focus:ring-[#D61C4E]/5 rounded-xl text-gray-900 text-sm font-medium placeholder:text-gray-400 placeholder:font-medium transition-all shadow-inner outline-none disabled:opacity-50"
                                    />
                                </div>

                                {/* Submit button */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-[#D61C4E] text-white py-3 rounded-xl font-black uppercase tracking-[3px] text-xs shadow-lg shadow-[#D61C4E]/20 hover:bg-black hover:shadow-none transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {buttonLabel}
                                    {status === "idle" && (
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                        </svg>
                                    )}
                                </button>

                                {/* Trust row */}
                                <div className="flex items-center justify-center lg:justify-start gap-3 mt-0.5">
                                    <div className="flex -space-x-2">
                                        {[1, 2, 3].map((i) => (
                                            <div
                                                key={i}
                                                className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 overflow-hidden"
                                            >
                                                <div className="w-full h-full bg-gradient-to-tr from-[#D61C4E] to-gray-400 opacity-50" />
                                            </div>
                                        ))}
                                    </div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                        Trusted by 50,000+ users
                                    </span>
                                </div>

                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest text-center">
                                    No spam. Just pure savings.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Newsletter;
